import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import styled from "styled-components";
import { ProductShowcase } from "./ProductShowcase";
import { Trans } from "@coworker/locales";
import { parseProduct } from "../../services/products.service";
import { useAllSalesLocationsForArticleQuery } from "../../hooks/Locations/useAllSalesLocationsForArticleQuery";
import { useAvailableElevatedStock } from "../../hooks/useAvailableElevatedStock"; // TODO: Change to use from tasks-service only!
import trackerHelper from "../../helpers/tracker";
import { useProductQualityTeamId } from "../../hooks/useProductQualityTeamId";
import { useCountryId, useStoreId } from "../../core/auth/useLoggedInUser";
import { isRDTDevice } from "@coworker/reusable/helpers/devices";
import { generateLivligProductLink } from "@coworker/reusable/helpers/livligIntegration";
import { formatProductIdWithDots } from "@coworker/reusable/formatters/product.formatter";
import featureNames from "@coworker/enums/featureNames";
import tracker from "../../helpers/tracker";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { PositionedLoaderIcon } from "@coworker/reusable/Loader";
import { ArticleError } from "@coworker/apprestructured/src/shared/components/ArticleError/ArticleError";
import { useInputPopup } from "../../components/InputPopup";
import { usePIPHome } from "@coworker/apprestructured/src/shared/hooks/item/usePIPHome";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import FixaCarousel from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { ItemError } from "@coworker/apprestructured/src/shared/enums/itemError";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { useItemExpectedDelivery } from "@coworker/apprestructured/src/shared/hooks/item/useItemExpectedDelivery";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useRelatedTasksForProduct } from "@coworker/apprestructured/src/tasks/hooks/useRelatedTasksForProduct";
import { CopyToClipboardButton } from "../../components/RoomSettings/Atoms/CopyToClipboardButton";
import {
  ProductInfo,
  DistancedRow,
  Title,
  Subtitle,
  InformationWrapper,
  ProductIdAndPriceWrapper,
  NumberWrapper,
  NumberBadge,
} from "./ProductShowcase";
import { useCombinationArticle } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames.js";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes.js";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState.js";

const ImageWrapper = styled("div")`
  margin: 16px 24px 6px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CarouselImage = styled(FixaImage)`
  height: 250px;
  object-fit: contain;
`;

const RelatedTasks = styled(FixaListViewItem)`
  span {
    color: var(--new);
  }
`;

function getLocationInfo(location, locationDescription) {
  if (location && locationDescription)
    return `${location} • ${locationDescription}`;
  else if (location) return `${location}`;
  else return "";
}

let livligLinkClicked = false;

const PIPHomeViewInner = ({
  productData,
  short_id,
  type,
  isBreathTakingItem,
  homeBaseLocation,
  homeBaseDescription,
  onAction,
  isPriceOrCurrencyMissing,
}) => {
  const full_id = type + short_id;
  const {
    available,
    elevated,
    color,
    loading: isStockLoading,
  } = useAvailableElevatedStock(full_id);
  const store_id = useStoreId();
  const isRDT = isRDTDevice();
  const hasActiveProductQualityTeam = !!useProductQualityTeamId();
  const { data: relatedTasks } = useRelatedTasksForProduct(short_id);
  const navigate = useFixaNavigate();

  const { data: itemDeliveryData, isLoading: isDeliveryDataLoading } =
    useItemExpectedDelivery(short_id, store_id);
  const trackLinkDisplaySentIfLivligLinkHasNotBeenClicked = (
    _store_id,
    _short_id
  ) => {
    if (!livligLinkClicked) {
      tracker.livlig.trackLinkDisplay(_store_id, _short_id, featureNames.PIP);
    }
    livligLinkClicked = false;
  };

  useEffect(() => {
    return () => {
      trackLinkDisplaySentIfLivligLinkHasNotBeenClicked(store_id, short_id);
    };
  }, [store_id, short_id]);

  return (
    <>
      <ProductShowcase
        name={productData?.name}
        price={productData?.salesPrice?.price}
        currency={productData?.salesPrice?.currency}
        priceUnit={productData?.salesPrice?.unit}
        originalPrice={productData?.salesPrice?.previousPrice?.price}
        isBreathTakingItem={isBreathTakingItem}
        isFamilyPrice={productData?.salesPrice?.isFamilyPrice}
        newLowerPrice={productData?.salesPrice?.newLowerPrice}
        description={productData.description || ""}
        formattedNumber={formatProductIdWithDots(productData.no)}
        elevated={elevated}
        color={color}
        available={available}
        isStockLoading={isStockLoading}
        itemDeliveryData={itemDeliveryData}
        isDeliveryDataLoading={isDeliveryDataLoading}
      />
      <FixaListView id={productData.no}>
        {relatedTasks?.list?.length > 0 && (
          <RelatedTasks
            id="test"
            title={
              <Trans count={relatedTasks.list.length}>
                {relatedTasks.list.length === 1
                  ? "relatedTasksCountString"
                  : "relatedTasksCountString_plural"}
              </Trans>
            }
            inset
            control="navigational"
            controlIcon={"chevronRight"}
            emphasised
            onClick={() => {
              tracker.trackGlobalActionClick("related_tasks");
              navigate(`/tasks/related/${productData.no}`);
            }}
          />
        )}
        <FixaListViewItem
          id="test"
          title={<Trans>addonOrderString</Trans>}
          inset
          control="navigational"
          controlIcon={"chevronRight"}
          emphasised
          onClick={() => onAction("addon", isPriceOrCurrencyMissing)}
        />
        <FixaListViewItem
          id="test"
          title={<Trans>productString</Trans>}
          inset
          control="navigational"
          controlIcon={"chevronRight"}
          emphasised
          onClick={() => onAction("product")}
        />
        {hasActiveProductQualityTeam && (
          <FixaListViewItem
            id="test"
            title={<Trans>productQualityReportString</Trans>}
            inset
            control="navigational"
            controlIcon={"chevronRight"}
            emphasised
            onClick={() => onAction("product_quality")}
          />
        )}
        <FixaListViewItem
          id="test"
          title={<Trans>salesLocationsAndStockString</Trans>}
          description={
            !["", 0].includes(available) &&
            getLocationInfo(homeBaseLocation, homeBaseDescription)
          }
          inset
          control="navigational"
          controlIcon={"chevronRight"}
          emphasised
          onClick={() => {
            tracker.trackGlobalActionClick("stock");
            navigate(`/product/${full_id}/stock`, { title: productData.name });
          }}
        />
        {!isRDT && (
          <FixaListViewItem
            id="test"
            title={<Trans>livligLinkWidgetTitle</Trans>}
            inset
            control="navigational"
            emphasised
            onClick={() => {
              tracker.trackGlobalActionClick("livlig_link");
              livligLinkClicked = true;
              tracker.livlig.trackLinkClick(
                store_id,
                short_id,
                featureNames.PIP
              );
              window.open(
                generateLivligProductLink(store_id, short_id),
                "_blank"
              );
            }}
          />
        )}
      </FixaListView>
    </>
  );
};

export function PIPHomeView() {
  const { full_id: maybeFullId } = useParams();
  const navigate = useFixaNavigate();
  const [shortId, type] = parseProduct(maybeFullId);
  const storeId = useStoreId();
  const userCountryId = useCountryId();
  const productQualityTeamId = useProductQualityTeamId();
  const { closePopup } = useInputPopup();

  const { data: pipHomeData, isLoading } = usePIPHome(shortId);
  const { data: relatedTasks } = useRelatedTasksForProduct(shortId);

  const isPriceOrCurrencyMissing =
    !pipHomeData?.salesPrice?.price || !pipHomeData?.salesPrice?.currency;

  const { data: combinationArticle } = useCombinationArticle(
    storeId,
    userCountryId,
    shortId,
    { enabled: type === "SCI" }
  );

  const { data: locationsData, isLoading: isLoadingLocations } =
    useAllSalesLocationsForArticleQuery(
      type && shortId ? `${type}${shortId}` : ""
    );

  const [showArticleErrorModal, setShowArticleErrorModal] = useState(false);
  const toggleArticleErrorModal = () =>
    setShowArticleErrorModal((prev) => !prev);

  const {
    location: homeBaseLocation,
    locationDescription: homeBaseDescription,
    descriptive: homeDescriptive,
  } = (!isLoadingLocations &&
    locationsData?.find((l) => l.isHomeBase || l.isHome)) ||
  {};
  const { data: taskTypes } = useAllTaskTypes();
  const [, setSearchParams] = useSearchParamState();

  function getTaskTypeId(taskTypeName) {
    return taskTypes?.find((type) => type.name === taskTypeName)?.id;
  }

  const onAction = (which, isPriceMissing = false) => {
    if (!which) return;

    if (which === "addon" && isPriceMissing) {
      setShowArticleErrorModal(true);
      return;
    }

    closePopup();
    tracker.trackGlobalActionClick(which);
    const productIssueId = getTaskTypeId(TaskTypeNames.PRODUCT_ISSUE);

    if (productIssueId && which === "product") {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(productIssueId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              item: {
                no: shortId,
                type: type,
              },
            },
          })
        );
      });
    } else {
      navigate(`/task/new/${which}`, {
        state: {
          product: { supplierNumber: "", product: shortId, type },
          qualityTeamId: productQualityTeamId,
        },
      });
    }
  };

  useEffect(() => {
    trackerHelper.trackPIPEntered();
  }, []);

  const { images, bigImage } = pipHomeData?.productImages || {};
  const chosenImages = useMemo(
    () => (images?.length ? images : [bigImage]),
    [images, bigImage]
  );

  const productData = useMemo(
    () =>
      !isLoading && pipHomeData
        ? {
            name: pipHomeData.name,
            description: pipHomeData.description,
            measurements: pipHomeData.measurements,
            no: pipHomeData.no,
            shortId,
            salesPrice: pipHomeData.salesPrice,
            isFamilyPrice: pipHomeData?.salesPrice?.isFamilyPrice,
            newLowerPrice: pipHomeData?.salesPrice?.newLowerPrice,
            location: pipHomeData.location || "?",
            locationDescription: pipHomeData.locationDescription || "?",
          }
        : {},
    [isLoading, pipHomeData, shortId]
  );

  if (type === "SCI") {
    const hasCombination = combinationArticle?.length > 0;
    const combinationData = hasCombination ? combinationArticle[0] : null;

    return (
      <FullScreenPopup
        noPadding
        testId="pip-home-view"
        linkbar={
          <Linkbar
            currentLabel={combinationData?.name.split(" ")[0] || ""}
            loading={hasCombination ? !combinationData?.name : true}
          />
        }
      >
        <ImageWrapper>
          <CarouselImage key="images" src="" fallback="" />
        </ImageWrapper>
        <ProductInfo>
          <ProductIdAndPriceWrapper>
            <NumberWrapper>
              <NumberBadge>{combinationData?.noFormatted || ""}</NumberBadge>
              <CopyToClipboardButton
                valueForClipboard={shortId.replace(/\./g, "")}
                onClick={() => {}}
              />
            </NumberWrapper>
          </ProductIdAndPriceWrapper>
          <DistancedRow>
            <InformationWrapper>
              <div>
                <Title>{combinationData?.name.split(" ")[0]}</Title>
                <Subtitle>
                  {combinationData
                    ? combinationData.name
                        .split(" ")
                        .slice(2)
                        .filter((part) => part !== "0")
                        .join(" ")
                    : ""}
                </Subtitle>
              </div>
            </InformationWrapper>
          </DistancedRow>
        </ProductInfo>
        <FixaListView id={productData.no}>
          {relatedTasks?.list?.length > 0 && (
            <RelatedTasks
              key="related-tasks"
              title={
                <Trans count={relatedTasks.list.length}>
                  {relatedTasks.list.length === 1
                    ? "relatedTasksCountString"
                    : "relatedTasksCountString_plural"}
                </Trans>
              }
              inset
              control="navigational"
              controlIcon="chevronRight"
              emphasised
              onClick={() => navigate(`/tasks/related/${combinationData?.no}`)}
            />
          )}
        </FixaListView>
      </FullScreenPopup>
    );
  }

  return (
    <FullScreenPopup
      noPadding
      testId="pip-home-view"
      linkbar={
        <Linkbar currentLabel={productData.name} loading={!productData.name} />
      }
    >
      {(isLoading || isLoadingLocations) && <PositionedLoaderIcon />}
      {!isLoading && !isLoadingLocations && (
        <>
          <ImageWrapper>
            <FixaCarousel id="pip-images">
              {chosenImages.map((image, index) => (
                <CarouselImage
                  key={`image${index}`}
                  src={image ?? ""}
                  fallback={image === ""}
                />
              ))}
            </FixaCarousel>
          </ImageWrapper>
          <PIPHomeViewInner
            productData={productData}
            short_id={productData.shortId}
            type={type}
            isBreathTakingItem={pipHomeData?.isBreathTakingItem ?? ""}
            homeBaseLocation={homeBaseLocation}
            homeBaseDescription={homeBaseDescription || homeDescriptive}
            onAction={onAction}
            isPriceOrCurrencyMissing={isPriceOrCurrencyMissing}
          />
        </>
      )}
      <ArticleError
        itemErrorType={ItemError.ITEM_PRICE_MISSING}
        openArticleErrorModal={showArticleErrorModal}
        onClose={toggleArticleErrorModal}
      />
    </FullScreenPopup>
  );
}
